import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ErpCatalogPanel } from '@/components/template-builder/ErpCatalogPanel';
import { TemplateDocTypeEditorialPanel } from '@/components/template-builder/TemplateDocTypeEditorialPanel';
import { TemplateCockpitDiagnosticsPanel } from '@/components/template-builder/TemplateCockpitDiagnosticsPanel';
import { TemplateBuilderRuntimePreviewCard } from '@/components/template-builder/TemplateBuilderRuntimePreviewCard';
import { Eye, Layers, LayoutTemplate, PenTool, Star, Trash2 } from 'lucide-react';


const getElementLabel = (element) => {
  if (!element) return 'Elemento';
  if (element.type === 'variable') return element.variablePath || 'Variável';
  if (element.type === 'table') return element.datasource || 'Tabela';
  if (element.type === 'text') return element.text || 'Texto';
  return element.type || 'Elemento';
};


export const TemplateBuilderReorganizedShell = ({
  activeDocTypeFilter,
  activeModuleFilter,
  activeSearchFilter,
  clearAllFilters,
  diagnostics,
  documentTypeOptions,
  getDocumentLabel,
  getLocationName,
  getModuleMeta,
  getPrimaryTemplateModule,
  getTemplateDecisionGuidance,
  getTemplateDisplayOptionLabel,
  moduleOptions,
  onDeleteTemplate,
  onOpenFreeformEditor,
  onOpenPreviewDialog,
  onPreviewTemplate,
  previewError,
  previewLoading,
  previewMeta,
  previewUrl,
  onSelectTemplate,
  onSetDefaultTemplate,
  selectedLayout,
  selectedTemplate,
  sortedTemplates,
  templateStats,
  updateSearchFilter,
  formatDateTime,
}) => {
  const selectedElements = selectedLayout?.elements || [];

  return (
    <div className="grid min-h-[760px] grid-cols-12 gap-3 xl:h-[calc(100vh-286px)] xl:overflow-hidden" data-testid="template-builder-reorganized-shell">
      <Card className="col-span-12 flex min-h-0 flex-col xl:col-span-3" data-testid="template-builder-shell-left-panel">
        <CardHeader className="border-b px-3 py-2">
          <CardTitle className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4" /> Registry compacto
            </span>
            <Badge variant="outline" data-testid="template-builder-shell-registry-count-badge">{sortedTemplates.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <div className="space-y-2 border-b px-3 py-2">
            <div className="grid gap-2">
              <Input
                value={activeSearchFilter}
                onChange={(event) => updateSearchFilter('q', event.target.value)}
                placeholder="Pesquisar template, módulo ou doc_type"
                data-testid="template-builder-shell-search-input"
              />
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                <Select value={activeModuleFilter} onValueChange={(value) => updateSearchFilter('module_group', value)}>
                  <SelectTrigger data-testid="template-builder-shell-module-filter">
                    <SelectValue placeholder="Módulo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos os módulos</SelectItem>
                    {moduleOptions.map((item) => (
                      <SelectItem key={`shell-module-${item.value}`} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={activeDocTypeFilter} onValueChange={(value) => updateSearchFilter('doc_type', value)}>
                  <SelectTrigger data-testid="template-builder-shell-doc-type-filter">
                    <SelectValue placeholder="Doc type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos os doc_types</SelectItem>
                    {documentTypeOptions.map((item) => (
                      <SelectItem key={`shell-doc-type-${item.value}`} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={clearAllFilters} data-testid="template-builder-shell-clear-filters-button">
                Limpar filtros
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[10px]" data-testid="template-builder-shell-registry-stats">
              <div className="rounded-lg border bg-slate-50 px-2 py-1.5">
                <p className="uppercase tracking-[0.16em] text-muted-foreground">Totais</p>
                <p className="mt-1 font-semibold">{templateStats.total}</p>
              </div>
              <div className="rounded-lg border bg-slate-50 px-2 py-1.5">
                <p className="uppercase tracking-[0.16em] text-muted-foreground">Visíveis</p>
                <p className="mt-1 font-semibold">{templateStats.visible}</p>
              </div>
            </div>
          </div>

          <ScrollArea className="min-h-0 flex-1" data-testid="template-builder-shell-registry-scroll">
            <div className="space-y-2 px-3 py-2">
              {sortedTemplates.map((template) => {
                const moduleMeta = getModuleMeta(getPrimaryTemplateModule(template));
                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => onSelectTemplate(template)}
                    className={`w-full rounded-lg border p-2 text-left transition-colors ${selectedTemplate?.id === template.id ? 'border-blue-500 bg-blue-50' : 'border-border hover:bg-muted/40'}`}
                    data-testid={`template-builder-shell-template-${template.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[12px] font-semibold">{template.name}</p>
                        <p className="truncate text-[10px] text-muted-foreground">{getTemplateDisplayOptionLabel(template)}</p>
                      </div>
                      {template.is_default && <Star className="mt-0.5 h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Badge className={moduleMeta.badgeClass}>{moduleMeta.label}</Badge>
                      <Badge variant="outline">{getDocumentLabel(template.document_type)}</Badge>
                      {template.is_module_legacy && <Badge className="bg-amber-100 text-amber-900">Legacy</Badge>}
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="col-span-12 flex min-h-0 flex-col xl:col-span-5" data-testid="template-builder-shell-center-panel">
        <CardHeader className="border-b px-3 py-2">
          <CardTitle className="flex items-center justify-between gap-3 text-sm">
            <span className="flex items-center gap-2">
              <Layers className="h-4 w-4" /> Estrutura + edição rápida
            </span>
            {selectedTemplate && <Badge variant="outline" data-testid="template-builder-shell-selected-doc-type-badge">{selectedTemplate.document_family || selectedTemplate.document_type}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col space-y-3 px-3 py-3">
          {!selectedTemplate ? (
            <div className="flex min-h-[420px] flex-1 flex-col items-center justify-center text-center text-muted-foreground" data-testid="template-builder-shell-empty-state">
              <LayoutTemplate className="mb-3 h-12 w-12 opacity-30" />
              <p className="text-sm font-medium">Seleccione um template para abrir o cockpit reorganizado.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-slate-50 px-3 py-3" data-testid="template-builder-shell-selected-header">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold">{selectedTemplate.name}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">{selectedTemplate.document_family || selectedTemplate.document_type} · {selectedTemplate.origin_context || getPrimaryTemplateModule(selectedTemplate)} · {getLocationName(selectedTemplate)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => onOpenFreeformEditor(selectedTemplate)} data-testid="template-builder-shell-open-editor-button">
                    <PenTool className="mr-1 h-4 w-4" /> Editor
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onPreviewTemplate(selectedTemplate)} data-testid="template-builder-shell-preview-button">
                    <Eye className="mr-1 h-4 w-4" /> Preview
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onSetDefaultTemplate(selectedTemplate.id)} data-testid="template-builder-shell-set-default-button">
                    <Star className="mr-1 h-4 w-4" /> Padrão
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => onDeleteTemplate(selectedTemplate.id)} data-testid="template-builder-shell-delete-button">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-4" data-testid="template-builder-shell-selected-summary-grid">
                {[
                  { key: 'origin', label: 'Origin context', value: selectedTemplate.origin_context || getPrimaryTemplateModule(selectedTemplate) },
                  { key: 'family', label: 'Família', value: selectedTemplate.document_family || selectedTemplate.document_type },
                  { key: 'elements', label: 'Elementos', value: String(selectedElements.length) },
                  { key: 'updated', label: 'Actualizado', value: formatDateTime(selectedTemplate.updated_at || selectedTemplate.created_at) },
                ].map((item) => (
                  <div key={item.key} className="rounded-lg border bg-white px-3 py-2" data-testid={`template-builder-shell-summary-${item.key}`}>
                    <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
                    <p className="mt-1 text-[11px] font-semibold leading-snug">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid min-h-0 gap-3 xl:grid-cols-[0.95fr_1.05fr]">
                <div className="rounded-xl border min-h-0" data-testid="template-builder-shell-structure-card">
                  <div className="flex items-center justify-between border-b px-3 py-2">
                    <div>
                      <p className="text-sm font-semibold">Estrutura do template</p>
                      <p className="text-[11px] text-muted-foreground">Leitura directa do layout activo para reduzir voltas na operação.</p>
                    </div>
                    <Badge variant="outline">{selectedElements.length}</Badge>
                  </div>
                  <ScrollArea className="min-h-[260px] xl:h-[clamp(260px,32vh,420px)]" data-testid="template-builder-shell-structure-scroll">
                    <div className="space-y-2 px-3 py-2">
                      {selectedElements.length > 0 ? selectedElements.map((element, index) => (
                        <div key={element.id || `${element.type}-${index}`} className="rounded-lg border bg-slate-50 px-3 py-2 text-[11px]" data-testid={`template-builder-shell-structure-item-${index}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium">#{index + 1} · {element.type}</p>
                            <Badge variant="outline">x:{Math.round(element.x || 0)} y:{Math.round(element.y || 0)}</Badge>
                          </div>
                          <p className="mt-1 truncate text-muted-foreground">{getElementLabel(element)}</p>
                        </div>
                      )) : (
                        <div className="rounded-lg border border-dashed p-3 text-[11px] text-muted-foreground">Ainda sem elementos no layout.</div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <TemplateBuilderRuntimePreviewCard
                  getLocationName={getLocationName}
                  onOpenDialog={onOpenPreviewDialog}
                  onRefreshPreview={onPreviewTemplate}
                  previewError={previewError}
                  previewLoading={previewLoading}
                  previewMeta={previewMeta}
                  previewUrl={previewUrl}
                  selectedTemplate={selectedTemplate}
                />
              </div>

              <div className="grid min-h-0 gap-3 xl:grid-cols-[0.8fr_1.2fr]">
                <div className="rounded-xl border bg-white px-3 py-3" data-testid="template-builder-shell-resolution-card">
                  <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Resolução activa</Label>
                  <p className="mt-2 text-[11px] font-semibold leading-snug">{selectedTemplate.name} · {selectedTemplate.origin_context || getPrimaryTemplateModule(selectedTemplate)}</p>
                  <p className="mt-2 text-[11px] text-muted-foreground">Shell reorganizado com estrutura e preview real lado a lado para reduzir voltas entre edição e validação.</p>
                </div>
                <TemplateDocTypeEditorialPanel
                  previewMeta={previewMeta}
                  selectedLayout={selectedLayout}
                  selectedTemplate={selectedTemplate}
                />
                <div className="rounded-xl border bg-white px-3 py-3" data-testid="template-builder-shell-json-card">
                  <Label className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">Snapshot JSON</Label>
                  <pre className="mt-2 max-h-56 overflow-auto rounded-lg bg-slate-50 p-3 text-[10px] leading-tight text-slate-700">{JSON.stringify({
                    canvasWidth: selectedLayout?.canvasWidth,
                    canvasHeight: selectedLayout?.canvasHeight,
                    elements: selectedElements.slice(0, 8).map((element) => ({
                      id: element.id,
                      type: element.type,
                      variablePath: element.variablePath,
                      datasource: element.datasource,
                      x: Math.round(element.x || 0),
                      y: Math.round(element.y || 0),
                    })),
                  }, null, 2)}</pre>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="col-span-12 min-h-0 space-y-3 xl:col-span-4 xl:overflow-auto" data-testid="template-builder-shell-right-panel">
        <TemplateCockpitDiagnosticsPanel
          diagnostics={diagnostics}
          title="Diagnóstico governado"
          description="Warnings, duplicações e legado sem sair do cockpit reorganizado."
          testIdPrefix="template-builder-shell-diagnostics"
        />
        <Card data-testid="template-builder-shell-contract-card">
          <CardHeader className="border-b px-3 py-2">
            <CardTitle className="text-sm">Contrato / catálogo</CardTitle>
          </CardHeader>
          <CardContent className="px-3 py-3">
            <ErpCatalogPanel
              title="Contrato ERP"
              description="Catálogo canónico inline para localizar campos e datasources sem sair do cockpit."
              editable={false}
              testIdPrefix="template-builder-shell-erp-catalog"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};